import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccountRegion } from "@shared/types/account";
import type { AccountRegionInput } from "@shared/validation/accountRegion.schema";
import { apiClient, ApiError } from "./client";

/** The family's market + PH city. Its own key, so it never touches the auth query. */
export const accountRegionKey = ["account", "region"] as const;

/**
 * Everything the server picks by `users.market` (or the city): season cards,
 * community circles and the member's market, stories (PH-only guides),
 * registry templates (Milk-Code-safe in PH) and the crisis/support lines. A
 * region change refetches them all, so no screen keeps the other market's data.
 */
const MARKET_DEPENDENT_KEYS = [
  ["seasonal"],
  ["community"],
  ["stories"],
  ["registry"],
  ["safeSpace", "support"],
] as const;

export function useAccountRegion(enabled = true) {
  return useQuery<AccountRegion, ApiError>({
    queryKey: accountRegionKey,
    queryFn: () => apiClient<AccountRegion>("/api/account/region"),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateAccountRegion() {
  const queryClient = useQueryClient();
  return useMutation<AccountRegion, ApiError, AccountRegionInput>({
    mutationFn: (body) =>
      apiClient<AccountRegion>("/api/account/region", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(accountRegionKey, saved);
      for (const queryKey of MARKET_DEPENDENT_KEYS) void queryClient.invalidateQueries({ queryKey });
    },
  });
}
